import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, Phone, MessageCircle, ArrowLeft, Search, 
  Filter, DollarSign, Calendar, User, Home, Settings, Save, Loader2, CheckCircle, PhoneOff
} from 'lucide-react';
import api, { financialAPI } from '../services/api';

interface MorosoAccount {
  id: string;
  unit: string;
  propertyId: string;
  residentName: string;
  residentPhone: string;
  residentEmail: string;
  monthlyFee: number;
  lastPaymentDate: string;
  monthsOverdue: number;
  totalDebt: number;
  status: 'moroso' | 'critico' | 'severo';
}

const DEFAULT_MONTHLY_FEE = 3500;
const MOROSO_THRESHOLD_MONTHS = 2; // A partir de 2 meses se considera moroso

// Función para calcular morosidad automáticamente
const calculateMorosidad = (
  lastPaymentDate: string, 
  monthlyFee: number
): { monthsOverdue: number; totalDebt: number; status: 'moroso' | 'critico' | 'severo' } => {
  const lastPayment = new Date(lastPaymentDate);
  const today = new Date();
  
  // Calcular diferencia en meses
  let monthsDiff = (today.getFullYear() - lastPayment.getFullYear()) * 12;
  monthsDiff += today.getMonth() - lastPayment.getMonth();
  
  // Si ya pasó el día del mes del último pago, sumar 1 mes
  if (today.getDate() > lastPayment.getDate()) {
    monthsDiff += 1;
  }
  
  const monthsOverdue = Math.max(0, monthsDiff);
  const totalDebt = monthsOverdue * monthlyFee;
  
  // Determinar status
  let status: 'moroso' | 'critico' | 'severo' = 'moroso';
  if (monthsOverdue >= 6) {
    status = 'severo';
  } else if (monthsOverdue >= 4) {
    status = 'critico';
  }
  
  return { monthsOverdue, totalDebt, status };
};

// Mock data removed — page fetches real data from financialAPI.getMorosos

export default function Morosos() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<MorosoAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'moroso' | 'critico' | 'severo'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [thresholdMonths, setThresholdMonths] = useState(MOROSO_THRESHOLD_MONTHS);

  // Morosidad config state
  const [showConfig, setShowConfig] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSuccess, setConfigSuccess] = useState<string | null>(null);
  const [config, setConfig] = useState({
    late_fee_percentage: 5,
    grace_days: 0,
    months_threshold: 2,
  });

  useEffect(() => {
    loadMorosos();
    loadMorosidadConfig();
  }, [thresholdMonths]);

  const loadMorosidadConfig = async () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const condoId = userData.condoId;
    if (!condoId) return;
    setConfigLoading(true);
    setConfigError(null);
    try {
      const res = await api.get(`/condos/${condoId}/morosidad-config`);
      const raw = res.data?.data ?? res.data;
      // BUG FIX (2026-08-21): backend nests the saved values under
      // `morosidadConfig`; normalize so the panel shows saved values.
      const data = raw?.morosidadConfig ?? raw;
      if (data) {
        const monthsThreshold = data.months_threshold
          ?? (Array.isArray(data.escalation_steps) && data.escalation_steps[0]?.day
                ? Math.max(1, Math.round(data.escalation_steps[0].day / 30))
                : 2);
        setConfig({
          late_fee_percentage: data.late_fee_percentage ?? 5,
          grace_days: data.grace_days ?? 0,
          months_threshold: monthsThreshold,
        });
        if (monthsThreshold) setThresholdMonths(monthsThreshold);
      }
    } catch (err: any) {
      // Config might not exist yet — use defaults
      console.error('Error loading morosidad config:', err);
    } finally {
      setConfigLoading(false);
    }
  };

  const saveMorosidadConfig = async () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const condoId = userData.condoId;
    if (!condoId) {
      setConfigError('Sin condominio asignado');
      return;
    }
    // Validation
    if (config.late_fee_percentage < 0 || config.late_fee_percentage > 100) {
      setConfigError('El recargo por mora debe estar entre 0 y 100%');
      return;
    }
    if (config.grace_days < 0 || config.grace_days > 365) {
      setConfigError('Los días de gracia deben estar entre 0 y 365');
      return;
    }
    if (config.months_threshold < 1 || config.months_threshold > 12) {
      setConfigError('Los meses para morosidad deben estar entre 1 y 12');
      return;
    }
    setConfigSaving(true);
    setConfigError(null);
    setConfigSuccess(null);
    try {
      await api.put(`/condos/${condoId}/morosidad-config`, {
        late_fee_percentage: config.late_fee_percentage,
        grace_days: config.grace_days,
        // F42: escalation_steps must be array of {day, fee} objects
        escalation_steps: [
          { day: config.months_threshold * 30, fee: config.late_fee_percentage },
        ],
      });
      setConfigSuccess('Configuración guardada correctamente');
      setThresholdMonths(config.months_threshold);
      setTimeout(() => setConfigSuccess(null), 3000);
    } catch (err: any) {
      setConfigError(err?.response?.data?.error || err?.message || 'Error al guardar configuración');
    } finally {
      setConfigSaving(false);
    }
  };

  const loadMorosos = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      if (!condoId) {
        setAccounts([]);
        setLoading(false);
        return;
      }
      const response = await financialAPI.getMorosos(condoId);
      // BUG FIX (2026-08-21): backend returns { success, data: [...] } and axios
      // wraps that again, so the array lives at response.data.data. Unwrap both
      // layers (same pattern as the complaints fix).
      let data = response.data?.data ?? response.data ?? response ?? [];
      // Ensure data is an array — API may return HTML error page or object on failure
      if (!Array.isArray(data)) {
        if (data && Array.isArray(data.results)) {
          data = data.results;
        } else if (data && Array.isArray(data.data)) {
          data = data.data;
        } else {
          data = [];
        }
      }
      // Calculate morosidad for each resident and filter by threshold
      const calculatedAccounts: MorosoAccount[] = data
        .map((resident: any) => {
          const calc = calculateMorosidad(resident.lastPaymentDate, resident.monthlyFee);
          return {
            id: resident.id,
            unit: resident.unit,
            propertyId: resident.propertyId,
            residentName: resident.residentName || resident.name,
            residentPhone: resident.residentPhone || resident.phone || '',
            residentEmail: resident.residentEmail || resident.email,
            monthlyFee: resident.monthlyFee,
            lastPaymentDate: resident.lastPaymentDate,
            ...calc
          };
        })
        .filter((account: MorosoAccount) => account.monthsOverdue >= thresholdMonths);
      setAccounts(calculatedAccounts);
    } catch (err) {
      console.error('Error loading morosos:', err);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(account => {
    // Filtro por status
    if (filter !== 'all' && account.status !== filter) return false;
    
    // Filtro por búsqueda
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        account.residentName.toLowerCase().includes(query) ||
        account.unit.toLowerCase().includes(query) ||
        account.residentEmail.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const stats = {
    total: filteredAccounts.length,
    totalDebt: filteredAccounts.reduce((sum, acc) => sum + acc.totalDebt, 0),
    averageDebt: filteredAccounts.length > 0 
      ? Math.round(filteredAccounts.reduce((sum, acc) => sum + acc.totalDebt, 0) / filteredAccounts.length)
      : 0,
    byStatus: {
      moroso: filteredAccounts.filter(a => a.status === 'moroso').length,
      critico: filteredAccounts.filter(a => a.status === 'critico').length,
      severo: filteredAccounts.filter(a => a.status === 'severo').length,
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'severo':
        return <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 text-sm font-bold rounded-full"><AlertTriangle className="w-3 h-3" /> Severo</span>;
      case 'critico':
        return <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 text-sm font-bold rounded-full"><AlertTriangle className="w-3 h-3" /> Crítico</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 text-sm font-bold rounded-full">Moroso</span>;
    }
  };

  const handleWhatsApp = (phone: string, name: string, debt: number) => {
    const message = `Hola ${name}, le escribimos de la administración de su condominio. Tiene un adeudo de $${debt.toLocaleString()}. Por favor póngase al corriente. Gracias.`;
    window.open(`https://wa.me/${phone.replace(/-/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const condoId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').condoId; }
    catch { return undefined; }
  })();

  if (!condoId) return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex items-center justify-center min-h-full">
        <div className="text-center">
          <div className="w-16 h-16 bg-[var(--v-surface-2)] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Home className="w-8 h-8 text-[var(--v-text-tertiary)]" />
          </div>
          <h2 className="text-xl font-bold text-[var(--v-text-primary)] mb-2">Sin condominio asignado</h2>
          <p className="text-[var(--v-text-secondary)]">Aún no se te ha asignado un condominio. Contacta al administrador para continuar.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-[var(--v-surface)] border-b border-[var(--v-border)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/building/dashboard')} className="p-2 text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[var(--v-text-primary)]">Morosos</h1>
              <p className="text-xs text-[var(--v-text-secondary)]">Cálculo automático de adeudos</p>
            </div>
          </div>
          <button
            onClick={() => setShowConfig(!showConfig)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
              showConfig ? 'bg-[#36CCD8] text-white' : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-3)]'
            }`}
          >
            <Settings className="w-4 h-4" />
            Configuración
          </button>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        {/* Configuración de Morosidad */}
        {showConfig && (
          <div className="bg-[var(--v-surface)] rounded-2xl border border-[var(--v-border)] shadow-sm p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#36CCD8]/10 rounded-xl flex items-center justify-center">
                <Settings className="w-5 h-5 text-[#36CCD8]" />
              </div>
              <div>
                <h2 className="font-bold text-[var(--v-text-primary)]">Configuración de Morosidad</h2>
                <p className="text-xs text-[var(--v-text-secondary)]">Define las reglas para el cálculo de morosidad y recargos</p>
              </div>
            </div>

            {configError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {configError}
              </div>
            )}
            {configSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {configSuccess}
              </div>
            )}

            {configLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-[#36CCD8]" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Meses para morosidad */}
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-1">
                    Meses para morosidad
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--v-text-tertiary)]" />
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={config.months_threshold}
                      onChange={(e) => setConfig({ ...config, months_threshold: Number(e.target.value) })}
                      className="w-full pl-10 pr-3 py-2 border border-[var(--v-border)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
                    />
                  </div>
                  <p className="text-xs text-[var(--v-text-secondary)] mt-1">Default: 2 meses</p>
                </div>

                {/* Recargo por mora % */}
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-1">
                    Recargo por mora (%)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--v-text-tertiary)]" />
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={config.late_fee_percentage}
                      onChange={(e) => setConfig({ ...config, late_fee_percentage: Number(e.target.value) })}
                      className="w-full pl-10 pr-3 py-2 border border-[var(--v-border)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
                    />
                  </div>
                  <p className="text-xs text-[var(--v-text-secondary)] mt-1">Default: 5% · Rango: 0-100%</p>
                </div>

                {/* Días de gracia */}
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-1">
                    Días de gracia
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--v-text-tertiary)]" />
                    <input
                      type="number"
                      min="0"
                      max="365"
                      value={config.grace_days}
                      onChange={(e) => setConfig({ ...config, grace_days: Number(e.target.value) })}
                      className="w-full pl-10 pr-3 py-2 border border-[var(--v-border)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
                    />
                  </div>
                  <p className="text-xs text-[var(--v-text-secondary)] mt-1">Default: 0 · Rango: 0-365</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={saveMorosidadConfig}
                disabled={configSaving || configLoading}
                className="flex items-center gap-2 px-4 py-2 bg-[#36CCD8] text-white rounded-xl font-medium hover:bg-[#2DB8C4] disabled:opacity-50 transition-colors"
              >
                {configSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {configSaving ? 'Guardando...' : 'Guardar configuración'}
              </button>
              <button
                onClick={() => setShowConfig(false)}
                className="px-4 py-2 border border-[var(--v-border)] text-[var(--v-text-secondary)] rounded-xl hover:bg-[var(--v-surface-2)] transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[var(--v-surface)] rounded-2xl p-5 border border-[var(--v-border)] shadow-sm">
            <p className="text-sm text-[var(--v-text-secondary)] mb-1">Total Morosos</p>
            <p className="text-3xl font-bold text-[var(--v-text-primary)]">{stats.total}</p>
          </div>
          <div className="bg-[var(--v-surface)] rounded-2xl p-5 border border-[var(--v-border)] shadow-sm">
            <p className="text-sm text-[var(--v-text-secondary)] mb-1">Adeudo Total</p>
            <p className="text-3xl font-bold text-red-500">${stats.totalDebt.toLocaleString()}</p>
          </div>
          <div className="bg-[var(--v-surface)] rounded-2xl p-5 border border-[var(--v-border)] shadow-sm">
            <p className="text-sm text-[var(--v-text-secondary)] mb-1">Promedio</p>
            <p className="text-3xl font-bold text-[#36CCD8]">${stats.averageDebt.toLocaleString()}</p>
          </div>
          <div className="bg-[var(--v-surface)] rounded-2xl p-5 border border-[var(--v-border)] shadow-sm">
            <p className="text-sm text-[var(--v-text-secondary)] mb-1">Críticos/Severos</p>
            <p className="text-3xl font-bold text-orange-500">{stats.byStatus.critico + stats.byStatus.severo}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[var(--v-surface)] rounded-2xl border border-[var(--v-border)] shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--v-text-tertiary)]" />
              <input
                type="text"
                placeholder="Buscar por nombre, unidad o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
              />
            </div>
            <div className="flex gap-2">
              {(['all', 'moroso', 'critico', 'severo'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-xl font-medium capitalize transition-all ${
                    filter === f
                      ? 'bg-[#36CCD8] text-white'
                      : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-3)]'
                  }`}
                >
                  {f === 'all' ? 'Todos' : f}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* List */}
        <div className="bg-[var(--v-surface)] rounded-2xl border border-[var(--v-border)] shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-[var(--v-text-secondary)]">Cargando...</div>
          ) : filteredAccounts.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-lg font-bold text-[var(--v-text-primary)] mb-1">¡Excelente!</p>
              <p className="text-[var(--v-text-secondary)]">No hay morosos con el criterio seleccionado</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--v-border)]">
              {filteredAccounts.map((account) => (
                <div key={account.id} className="p-6 hover:bg-[var(--v-surface-2)] transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        account.status === 'severo' ? 'bg-red-100' :
                        account.status === 'critico' ? 'bg-orange-100' : 'bg-amber-100'
                      }`}>
                        <User className={`w-6 h-6 ${
                          account.status === 'severo' ? 'text-red-600' :
                          account.status === 'critico' ? 'text-orange-600' : 'text-amber-600'
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-[var(--v-text-primary)]">{account.residentName}</h3>
                          {getStatusBadge(account.status)}
                        </div>
                        <p className="text-sm text-[var(--v-text-secondary)] flex items-center gap-1">
                          <Home className="w-4 h-4" /> Unidad {account.unit}
                        </p>
                        <p className="text-sm text-[var(--v-text-secondary)]">{account.residentEmail}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-[var(--v-text-secondary)]">
                            Último pago: <span className="font-medium">{new Date(account.lastPaymentDate).toLocaleDateString('es-MX')}</span>
                          </span>
                          <span className="text-[var(--v-text-secondary)]">
                            Mensualidad: <span className="font-medium">${account.monthlyFee.toLocaleString()}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-3xl font-bold text-red-500">${account.totalDebt.toLocaleString()}</p>
                        <p className="text-sm text-[var(--v-text-secondary)]">{account.monthsOverdue} meses de adeudo</p>
                      </div>
                      <div className="flex gap-2 items-center">
                        {account.residentPhone ? (
                          <>
                            <button 
                              onClick={() => handleWhatsApp(account.residentPhone, account.residentName, account.totalDebt)}
                              className="p-3 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors"
                              title="Enviar WhatsApp"
                            >
                              <MessageCircle className="w-5 h-5" />
                            </button>
                            <a 
                              href={`tel:${account.residentPhone}`}
                              className="p-3 bg-[#36CCD8] text-white rounded-xl hover:bg-[#2DB8C4] transition-colors"
                              title="Llamar"
                            >
                              <Phone className="w-5 h-5" />
                            </a>
                          </>
                        ) : (
                          /* Demo fix (2026-11-04): residents without a phone on
                             file rendered href="tel:undefined". Show a muted
                             'Sin telefono' placeholder instead of dead actions. */
                          <span 
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[var(--v-text-tertiary)] bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl"
                            title="Sin teléfono registrado"
                          >
                            <PhoneOff className="w-4 h-4" /> Sin teléfono
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-[var(--v-text-secondary)]">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span>Moroso (2-3 meses)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span>Crítico (4-5 meses)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span>Severo (6+ meses)</span>
          </div>
        </div>
      </main>
    </div>
  );
}
